import { BaseType, CostUnit } from '../types/Enums';
import { RELEVANT_BASE_TYPES } from './baseTypeUtils';

/**
 * Shared helpers for the semicolon-separated CSV files used by the
 * superadmin library pages (maintenance + lifetime). Export and import
 * must stay symmetric so an exported file can be re-imported unchanged.
 */

export const CSV_DELIMITER = ';';

/**
 * Escape a CSV field: wrap in double quotes if it contains the delimiter,
 * a quote, or a newline, and double any inner quotes.
 */
export function escapeCsvField(value: string): string {
    return /[";\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Split one CSV line into fields, honouring double-quoted fields
 * (which may contain the delimiter) and doubled inner quotes.
 * The inverse of escapeCsvField.
 */
export function parseCsvLine(line: string, delimiter: string = CSV_DELIMITER): string[] {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];

        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                current += ch;
            }
        } else if (ch === '"' && current === '') {
            inQuotes = true;
        } else if (ch === delimiter) {
            fields.push(current);
            current = '';
        } else {
            current += ch;
        }
    }

    fields.push(current);
    return fields.map((f) => f.replace(/\r$/, ''));
}

/**
 * Split file text into non-empty data lines, skipping the header row.
 */
export function getCsvDataLines(text: string): string[] {
    return text
        .split('\n')
        .slice(1)
        .filter((line) => line.trim() !== '');
}

/**
 * Trigger a browser download of a CSV file.
 * Uses CRLF line endings and a leading BOM (U+FEFF) so Excel detects UTF-8;
 * without the BOM Excel falls back to the legacy encoding and corrupts å, ä, ö.
 */
export function downloadCsv(filename: string, header: string, rows: string[]): void {
    const csvContent = [header, ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Today's date as YYYY-MM-DD for use in export filenames.
 */
export function csvDateSuffix(): string {
    return new Date().toISOString().split('T')[0];
}

// ============================================================
// Field parsers shared by the library imports
// ============================================================

const COST_UNIT_FROM_CSV: Record<string, CostUnit> = {
    m2: 'per_sqm',
    st: 'per_unit',
    m: 'per_lm',
};

const COST_UNIT_TO_CSV: Record<CostUnit, string> = {
    per_sqm: 'm2',
    per_unit: 'st',
    per_lm: 'm',
};

/** Human-readable list of accepted unit tokens, for error messages. */
export const CSV_COST_UNIT_TOKENS = Object.keys(COST_UNIT_FROM_CSV).join(', ');

export function parseCsvCostUnit(unit: string): CostUnit | undefined {
    return COST_UNIT_FROM_CSV[unit.trim().toLowerCase()];
}

export function formatCsvCostUnit(unit: CostUnit): string {
    return COST_UNIT_TO_CSV[unit] || '';
}

/**
 * Parse a whole-krona cost such as "2500", "2500 kr" or "2 500 kr".
 * Library costs are integers, so anything else — decimals ("2500,50"),
 * dot/comma separators ("2.500"), or non-numeric text — is rejected
 * with null so the caller can refuse the row.
 * Thousands may be grouped with a regular, non-breaking or narrow space.
 */
const CSV_COST_PATTERN = /^\s*(\d{1,3}(?:[ \u00A0\u202F]\d{3})+|\d+)\s*(?:kr)?\s*$/i;

export function parseCsvCost(cost: string): number | null {
    const match = cost.match(CSV_COST_PATTERN);
    if (!match) return null;
    return parseInt(match[1].replace(/[ \u00A0\u202F]/g, ''), 10);
}

/**
 * Format a whole-krona cost for export so parseCsvCost reads it back unchanged.
 */
export function formatCsvCost(cost: number): string {
    return `${Math.round(cost)} kr`;
}

/**
 * Parse a comma-separated list of base types, splitting into the ones that
 * are known (kept) and the ones that are not (reported to the user).
 */
export function parseCsvBaseTypes(baseTypesStr: string): { valid: BaseType[]; invalid: string[] } {
    const validBaseTypes = RELEVANT_BASE_TYPES as readonly string[];
    const valid: BaseType[] = [];
    const invalid: string[] = [];
    for (const s of baseTypesStr.split(',').map((s) => s.trim()).filter(Boolean)) {
        if (validBaseTypes.includes(s)) {
            valid.push(s as BaseType);
        } else {
            invalid.push(s);
        }
    }
    return { valid, invalid };
}

export function formatCsvBaseTypes(baseTypes: BaseType[]): string {
    return baseTypes.join(', ');
}
